import { getArrowBoundShapeIds } from './arrowSelectConnected.js';
import { membershipIdFromFrameShape } from './frameMembership.js';

export const EXTERNAL_ASK_LEDGER_MAX = 700;
export const EXTERNAL_ASK_LEDGER_HEADER = 'Board decisions (honor these):';

const CLOSED_KINDS = new Set(['decision', 'constraint']);

function idAliases(id) {
  const raw = String(id || '').trim();
  if (!raw) return [];
  const aliases = [raw];
  if (raw.startsWith('shape:frame-')) {
    aliases.push(raw.slice('shape:frame-'.length));
  } else if (raw.startsWith('frame:')) {
    aliases.push(raw.slice('frame:'.length));
  } else if (raw.startsWith('pin:')) {
    aliases.push(raw.slice('pin:'.length));
  } else if (!raw.startsWith('shape:')) {
    aliases.push(`shape:frame-${raw}`);
  }
  return aliases.filter(Boolean);
}

function addRef(refs, value) {
  for (const alias of idAliases(value)) refs.add(alias);
}

function findPinShape(editor, activityId) {
  if (!editor?.getCurrentPageShapes || !activityId) return null;
  const needle = String(activityId);
  return (editor.getCurrentPageShapes() || []).find(
    shape => shape?.type === 'pin-card' && String(shape.meta?.activityId || '') === needle
  ) || null;
}

/**
 * Ids that selected chips can match against a ledger row
 * (pin activityId, frame logical id, shape id, bound endpoints).
 */
export function collectSelectionRefsForLedger(selectionItems = [], editor = null) {
  const refs = new Set();

  const addShape = (shape) => {
    if (!shape?.id) return;
    addRef(refs, shape.id);
    if (shape.type === 'pin-card') addRef(refs, shape.meta?.activityId);
    if (shape.type === 'frame') {
      addRef(refs, membershipIdFromFrameShape(shape));
      addRef(refs, shape.meta?.boardFrameId);
    }
    if (shape.type === 'arrow' && editor) {
      for (const boundId of getArrowBoundShapeIds(editor, shape.id)) {
        addShape(editor.getShape?.(boundId));
      }
    }
  };

  for (const item of selectionItems || []) {
    addRef(refs, item?.id);
    if (item?.kind === 'user-pin') {
      addShape(findPinShape(editor, item.id));
      continue;
    }
    if (editor?.getShape && item?.id) addShape(editor.getShape(item.id));
  }

  return refs;
}

export function constraintTouchesSelection(constraint, selectionRefs) {
  if (!selectionRefs?.size) return false;
  const fields = [
    constraint.sourcePinId,
    constraint.sourceShapeId,
    constraint.aboutPinId,
    constraint.aboutShapeId,
    constraint.fromId,
    constraint.toId,
  ];
  return fields.some(value => (
    idAliases(value).some(alias => selectionRefs.has(alias))
  ));
}

function constraintText(constraint) {
  return String(constraint?.value || constraint?.claim || '').trim();
}

function isClosedConstraint(constraint) {
  return CLOSED_KINDS.has(String(constraint?.kind || '').trim().toLowerCase());
}

export function constraintMatchesTokens(constraint, tokens = []) {
  if (!tokens.length) return false;
  const haystack = [
    constraintText(constraint),
    constraint?.slot,
    constraint?.kind,
  ].filter(Boolean).join(' ').toLowerCase();
  return tokens.some(token => haystack.includes(String(token).toLowerCase()));
}

/**
 * Related ledger for an external ask.
 * Closed decisions: prefer token overlap; if none match, keep all closed
 * (they are few and must still be honored).
 * Other kinds: token overlap or a touch on the current selection/notes.
 */
export function selectRelatedConstraintsForExternalAsk(
  constraints = [],
  { selectionRefs = new Set(), tokens = [] } = {}
) {
  const active = (Array.isArray(constraints) ? constraints : [])
    .filter(c => c && c.active !== false && constraintText(c));

  const seen = new Set();
  const push = (list, constraint) => {
    const key = `${constraint.kind}|${constraint.slot}|${constraintText(constraint)}`.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    list.push(constraint);
  };

  const closed = [];
  const relatedClosed = [];
  for (const constraint of active) {
    if (!isClosedConstraint(constraint)) continue;
    push(closed, constraint);
    if (constraintMatchesTokens(constraint, tokens)) relatedClosed.push(constraint);
  }

  const extras = [];
  for (const constraint of active) {
    if (isClosedConstraint(constraint)) continue;
    const related = constraintMatchesTokens(constraint, tokens)
      || constraintTouchesSelection(constraint, selectionRefs);
    if (related) push(extras, constraint);
  }

  const keptClosed = relatedClosed.length ? relatedClosed : closed;
  return [...keptClosed, ...extras].slice(0, 12);
}

/**
 * Decisions/constraints always. Other kinds only when they touch the selection.
 */
export function selectConstraintsForExternalAsk(
  constraints = [],
  { selectionRefs = new Set(), tokens = [] } = {}
) {
  if (tokens.length) {
    return selectRelatedConstraintsForExternalAsk(constraints, { selectionRefs, tokens });
  }

  const active = (Array.isArray(constraints) ? constraints : [])
    .filter(c => c && c.active !== false && constraintText(c));

  const closed = [];
  const related = [];
  const seen = new Set();
  const push = (list, constraint) => {
    const key = `${constraint.kind}|${constraint.slot}|${constraintText(constraint)}`.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    list.push(constraint);
  };

  for (const constraint of active) {
    if (isClosedConstraint(constraint)) push(closed, constraint);
  }
  for (const constraint of active) {
    if (isClosedConstraint(constraint)) continue;
    if (constraintTouchesSelection(constraint, selectionRefs)) push(related, constraint);
  }

  return [...closed, ...related].slice(0, 12);
}

export function formatLedgerConstraintLine(constraint) {
  const text = constraintText(constraint);
  if (!text) return '';
  const kind = String(constraint.kind || '').trim().toLowerCase();
  const relation = Boolean(constraint.fromId && constraint.toId);
  const cluster = String(constraint.aboutKind || '').trim() === 'frame';
  let tag = kind || 'fact';
  if (relation) tag = kind && kind !== 'fact' ? `${kind} / relation` : 'relation';
  else if (cluster) tag = kind && kind !== 'fact' ? `${kind} / cluster` : 'cluster';
  return `- ${tag}: ${text}`.slice(0, 160);
}

export function formatRemovedClaims(constraints = [], max = 4) {
  const lines = (Array.isArray(constraints) ? constraints : [])
    .filter(item => item && item.active === false && item.withdrawn)
    .map(constraintText)
    .filter(Boolean)
    .slice(-max)
    .map(text => `- ${text}`.slice(0, 160));
  if (!lines.length) return '';
  return `Removed from the board (do not use):\n${lines.join('\n')}`;
}

/**
 * Fit ledger lines into maxChars. Never drop closed decisions until
 * non-closed rows are gone.
 */
export function fitExternalAskLedger(constraints = [], maxChars = EXTERNAL_ASK_LEDGER_MAX) {
  const cap = Math.max(0, Number(maxChars) || 0);
  if (!cap) return '';

  const rows = (constraints || []).map(constraint => ({
    keep: isClosedConstraint(constraint),
    line: formatLedgerConstraintLine(constraint),
  })).filter(row => row.line);

  const render = (list) => (
    list.length ? `${EXTERNAL_ASK_LEDGER_HEADER}\n${list.map(r => r.line).join('\n')}` : ''
  );

  let kept = [...rows];
  if (render(kept).length <= cap) return render(kept);

  kept = rows.filter(row => row.keep);
  if (render(kept).length <= cap) return render(kept);

  while (kept.length && render(kept).length > cap) {
    kept.pop();
  }
  return render(kept);
}
